import {
    cvtDiagnosticUploadsActions,
    isNoResultErrorType,
    isUploadedCvtDiagnosticFinished,
    isUploadedCvtDiagnosticProcessing,
    useCvtDiagnosticUploadsDispatch,
    useQmrCvtDiagnostic,
    useQmrValidCvtDiagnostics,
} from '@packages/contexts/qmr-cvt-diagnostic';
import { canChallengeCvtRecommendation, useQmrState } from '@packages/contexts/qmrs';
import { getLogger } from '@packages/core/logging';
import { RunDiagnostic } from '@packages/domain/cvt';
import { QmrStatusId } from '@packages/models/api';
import { CvtStatus } from '@packages/models/api/cvt';
import { AlertBar, Button, Icon, Typography } from '@packages/ui/shared';
import constants from '@web/config/constants';
import { CvtPrimaryConcernsModal } from '@web/cvt/components/cvt-primary-concerns-modal';
import { ErrorDetail } from '@web/cvt/components/error-detail';
import { container } from '@web/services/container';
import { cvtService, qmrsService, vinSessionService } from '@web/services/singletons';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuthState } from '@packages/contexts/auth';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import { Bounce, toast, ToastContainer } from 'react-toastify';

import { ChangeDiagnosticModal } from './change-diagnostic-modal';
import { CvtDiagnosticChallengeDetails } from './cvt-diagnostic-challenge-details';
import { CvtRepairResults } from './cvt-repair-results';
import { DiagnosticInfoModal } from './diagnostic-info-modal';
import { DiagnosticLink } from './diagnostic-link';
import { DiagnosticSummary } from './diagnostic-summary';

const logger = getLogger('qmr');

const [$runDiagnostic] = container.resolveOperations(RunDiagnostic);

const errorDetailStyle = { fontSize: '13px', margin: 0 };

const headerButtonTitleStyle = { fontSize: 13 };

const toastContainerStyle = { top: constants.headerHeight + constants.qmrHeaderHeight + 16 };

type CvtDiagnosticProps = {
    /**
     * Whether `qmr.capabilities` allow adding or changing the fail code, which also decides who can change the CVT
     * diagnostic.
     */
    canEditFailCode: boolean;

    /** Callback to show a toast message. */
    onShowToast: (message: string) => void;
};

/**
 * Renders the CVT Diagnostic component as part of the Fail Code section for a NQR that has a
 * CVT related fail code attached or requires one.
 */
export function CvtDiagnostic({ canEditFailCode, onShowToast }: CvtDiagnosticProps) {
    const { t } = useTranslation();
    const { cvtChallengeDetailsSaveCount, localPatch, qmr } = useQmrState();
    const { account } = useAuthState();
    const cvtDiagnosticUploadsDispatch = useCvtDiagnosticUploadsDispatch();
    const editRouteMatch = useMatch(`/${qmr?.isNqr ? 'nqrs' : 'qmrs'}/:displayIdentifier/edit`);
    const {
        diagnosticOptions,
        fetchDiagnostics,
        fetchMoreDiagnostics,
        hasFailedToLoadDiagnostics,
        hasFailedToLoadMoreDiagnostics,
        hasMoreDiagnostics,
        isLoadingDiagnostics,
        isLoadingMoreDiagnostics,
        loadDiagnostics,
    } = useQmrValidCvtDiagnostics({ qmrsService });

    const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
    const [showChangeDiagnosticModal, setShowChangeDiagnosticModal] = useState<boolean>(false);
    const [showPrimaryConcernsModal, setShowPrimaryConcernsModal] = useState<boolean>(false);

    const [cvtEligibleVin, setCvtEligibleVin] = useState<string>();
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const [closedAlertDiagnosticId, setClosedAlertDiagnosticId] = useState<string>();

    const isDraft = qmr?.qmrStatus.qmrStatusId === QmrStatusId.Draft;
    const isEditable = isDraft || Boolean(editRouteMatch);
    const canEditDiagnostic = canEditFailCode && isEditable;

    const hasAttachedDiagnostic = Boolean(qmr?.cvtDiagnosticsJobId);

    const { isUpdatingDiagnostic, updateDiagnostic, uploadedDiagnostic } = useQmrCvtDiagnostic({
        cvtService,
        isEnabled: canEditDiagnostic,
        onDiagnosticUpdate: showDiagnosticToast,
        qmrsService,
    });

    const isUploadedDiagnosticComplete = uploadedDiagnostic?.status === CvtStatus.RECEIVED_RESPONSE_FROM_SBR;
    const isUploadedDiagnosticFailed = Boolean(uploadedDiagnostic?.status) && !isUploadedDiagnosticComplete;

    /**
     * Whether to show the backend's message in a toast, while the diagnostic can be changed. As on the CVT page,
     * `SBR_UPLOAD_FAIL`'s is left out, since the backend retries that upload.
     */
    const showStsErrorResponse =
        canEditDiagnostic &&
        isUploadedDiagnosticFailed &&
        Boolean(uploadedDiagnostic?.stsErrorResponse) &&
        uploadedDiagnostic?.status !== CvtStatus.SBR_UPLOAD_FAIL;

    const showProcessingError = isUploadedDiagnosticFailed && !showStsErrorResponse;
    const isUploadedDiagnosticFinished = canEditDiagnostic && isUploadedCvtDiagnosticFinished(uploadedDiagnostic);
    const isUploadedNoResultDiagnosticAttached =
        isNoResultErrorType(uploadedDiagnostic) && !uploadedDiagnostic?.hasAttachError;

    const showUploadedDiagnosticAlert =
        isUploadedDiagnosticFinished &&
        !showStsErrorResponse &&
        closedAlertDiagnosticId !== uploadedDiagnostic?.diagnosticId;

    const isUploadedDiagnosticProcessing =
        canEditDiagnostic && isUploadedCvtDiagnosticProcessing({ qmr, uploadedDiagnostic });

    const showCvtDiagnosticChallengeDetails = hasAttachedDiagnostic
        ? canChallengeCvtRecommendation(qmr)
        : !isUploadedDiagnosticProcessing;

    function handleOpenInfoModal() {
        setShowInfoModal(true);
    }

    function handleCloseInfoModal() {
        setShowInfoModal(false);
    }

    function handleUploadedDiagnosticAlertClose() {
        setClosedAlertDiagnosticId(uploadedDiagnostic?.diagnosticId);
    }

    function handleChangeDiagnosticPress() {
        loadDiagnostics();
        setShowChangeDiagnosticModal(true);
    }

    function handleSearchDiagnosticPress() {
        loadDiagnostics();
        setShowChangeDiagnosticModal(true);
    }

    /**
     * Handles updating the diagnostic to the one selected in the change modal. Once it has, closes the modal and the
     * uploaded test's toast.
     */
    async function handleChangeDiagnostic(diagnosticId: string) {
        const isDiagnosticUpdated = await updateDiagnostic(diagnosticId);

        if (!isDiagnosticUpdated) {
            return;
        }

        setShowChangeDiagnosticModal(false);

        if (uploadedDiagnostic) {
            toast.dismiss(uploadedDiagnostic.diagnosticId);
        }
    }

    function handleChangeDiagnosticModalHide() {
        setShowChangeDiagnosticModal(false);
    }

    function handleReloadDiagnostics() {
        fetchDiagnostics();
    }

    function handleLoadMoreDiagnostics() {
        fetchMoreDiagnostics();
    }

    function handleRunNewDiagnostic() {
        setShowChangeDiagnosticModal(false);
        setShowPrimaryConcernsModal(true);
    }

    /** Returns to the change modal with the list it showed. */
    function handlePrimaryConcernsModalBack() {
        setShowPrimaryConcernsModal(false);
        setShowChangeDiagnosticModal(true);
    }

    function handlePrimaryConcernsModalClose() {
        setShowPrimaryConcernsModal(false);
    }

    /** A failed upload rejects, so the upload screen can show why. */
    async function handleRunDiagnostic(file: File, repairOrderNumber: string) {
        await runDiagnostic(file, repairOrderNumber);

        setShowPrimaryConcernsModal(false);
    }

    /**
     * Validates that the vehicle can run a CVT diagnostic test, uploads the file and runs it on the report's VIN, then
     * puts it in the store for the poll in place of the last test, and closes that test's toast. Rejects with why it
     * couldn't run, including while a VIN change waits for Save Changes, since a test can only attach to the saved VIN.
     */
    async function runDiagnostic(file: File, repairOrderNumber: string) {
        if (!qmr?.vin) {
            throw new Error('A VIN is required to run the test.');
        }

        if (localPatch?.vin !== undefined) {
            throw new Error('The VIN has changed. Save Changes before running a test.');
        }

        setIsUploading(true);

        try {
            await validateCvtEligibility(qmr.vin);

            const { stsDiagnosticId } = await $runDiagnostic({ file, repairOrderNumber, vin: qmr.vin });

            cvtDiagnosticUploadsDispatch(
                cvtDiagnosticUploadsActions.setUploadedDiagnostic({
                    uploadedDiagnostic: {
                        diagnosticId: stsDiagnosticId,
                        qmrId: qmr.qmrId,
                        uploadedAt: new Date(),
                    },
                })
            );

            if (uploadedDiagnostic) {
                toast.dismiss(uploadedDiagnostic.diagnosticId);
            }
        } catch (error) {
            logger.error('Failed to run the CVT diagnostic', { error, vin: qmr.vin });

            throw error instanceof Error ? error : new Error('An unexpected error occurred during upload.');
        } finally {
            setIsUploading(false);
        }
    }

    /**
     * Validates that the vehicle can run a CVT diagnostic test, since the backend won't attach a test to a VIN that
     * isn't CVT eligible. Asks the VIN Service only for a VIN not yet validated.
     */
    async function validateCvtEligibility(vin: string) {
        if (vin === cvtEligibleVin) {
            return;
        }

        const response = await vinSessionService.getVinData({ fetchVinParams: { vin }, ignoreCache: true });
        const vehicleDetails = response.success ? response.data[0] : undefined;

        if (!vehicleDetails) {
            throw new Error(
                "We couldn't validate whether this VIN supports CVT diagnostic tests. Please refresh and try again."
            );
        }

        if (!vehicleDetails.cvtEligible) {
            throw new Error(
                'This vehicle does not support CVT diagnostic tests. Check that the correct fail code is selected.'
            );
        }

        setCvtEligibleVin(vin);
    }

    function handleGoToStis() {
        if (!qmr?.vin) {
            return;
        }

        window.open(`/vin/${qmr.vin}/stis`, '_blank', 'noopener,noreferrer');
    }

    function showDiagnosticToast() {
        if (hasAttachedDiagnostic) {
            onShowToast(t('qmr:sections.failCode.cvtDiagnostic.changeDiagnosticToast', 'CVT diagnostic test changed'));
            return;
        }

        onShowToast(t('qmr:sections.failCode.cvtDiagnostic.addDiagnosticToast', 'CVT diagnostic test added'));
    }

    /**
     * Toasts the backend's message once the uploaded diagnostic fails with one, as the CVT page does. The toast's id
     * keeps a remount from showing it twice while it's open.
     */
    useEffect(() => {
        if (!showStsErrorResponse) {
            return;
        }

        toast.error(uploadedDiagnostic?.stsErrorResponse, {
            autoClose: false,
            closeButton: true,
            toastId: uploadedDiagnostic?.diagnosticId,
        });
    }, [showStsErrorResponse, uploadedDiagnostic?.diagnosticId, uploadedDiagnostic?.stsErrorResponse]);

    return (
        <div className="d-flex flex-column mt-4 gap-4">
            <div className="d-flex justify-content-between align-items-center">
                <Typography variant="caption3">
                    {t('qmr:sections.failCode.cvtDiagnostic.title', 'CVT App Diagnostic Results')}
                    <Typography color="redOne"> *</Typography>
                </Typography>
                {isEditable && (
                    <Button
                        iconLeft={<Icon color="blueOne" name="info" size={20} />}
                        onPress={handleOpenInfoModal}
                        size="small"
                        titleStyle={headerButtonTitleStyle}
                        variant="ghost-blue"
                    >
                        {t(
                            'qmr:sections.failCode.cvtDiagnostic.whyInclude',
                            'Why should a CVT diagnostic test be included?'
                        )}
                    </Button>
                )}
            </div>

            <AlertBar
                error={!isUploadedNoResultDiagnosticAttached}
                onClose={handleUploadedDiagnosticAlertClose}
                show={showUploadedDiagnosticAlert}
                success={isUploadedNoResultDiagnosticAttached}
            >
                {isUploadedNoResultDiagnosticAttached ? (
                    <ErrorDetail errorDetail={uploadedDiagnostic?.errorDetail ?? ''} style={errorDetailStyle} />
                ) : (
                    <div className="d-flex flex-column gap-2">
                        <Typography variant="label">
                            {t(
                                'qmr:sections.failCode.cvtDiagnostic.uploadedDiagnostic.notAttached',
                                "We couldn't attach diagnostic test {{diagnosticId}}.",
                                { diagnosticId: uploadedDiagnostic?.diagnosticId }
                            )}
                        </Typography>
                        {uploadedDiagnostic?.errorDetail && (
                            <ErrorDetail errorDetail={uploadedDiagnostic.errorDetail} style={errorDetailStyle} />
                        )}
                        {isUploadedDiagnosticComplete && (
                            <Typography>
                                <DiagnosticLink diagnosticId={uploadedDiagnostic?.diagnosticId} vin={qmr?.vin}>
                                    {t(
                                        'qmr:sections.failCode.cvtDiagnostic.uploadedDiagnostic.viewResults',
                                        'View test results'
                                    )}
                                </DiagnosticLink>
                            </Typography>
                        )}
                        {showProcessingError && (
                            <Typography>
                                {t(
                                    'qmr:sections.failCode.cvtDiagnostic.uploadedDiagnostic.processingError',
                                    "The test couldn't be processed."
                                )}
                            </Typography>
                        )}
                        {uploadedDiagnostic?.hasAttachError && (
                            <Typography>
                                {t(
                                    'qmr:sections.failCode.cvtDiagnostic.uploadedDiagnostic.attachError',
                                    'Something went wrong. Please try again.'
                                )}
                            </Typography>
                        )}
                    </div>
                )}
            </AlertBar>

            <DiagnosticSummary
                canEditDiagnostic={canEditDiagnostic}
                cvtDiagnosticsJobId={qmr?.cvtDiagnosticsJobId}
                isUploadedDiagnosticProcessing={isUploadedDiagnosticProcessing}
                onChangeDiagnosticPress={handleChangeDiagnosticPress}
                onSearchDiagnosticPress={handleSearchDiagnosticPress}
                sbrResponse={qmr?.sbrResponse}
                uploadedDiagnostic={uploadedDiagnostic}
                vin={qmr?.vin}
            />

            {account?.systemCapabilities.viewQmrRoleReviewOnList && <CvtRepairResults />}

            {showCvtDiagnosticChallengeDetails && (
                <CvtDiagnosticChallengeDetails
                    isDisabled={isUploadedDiagnosticProcessing}
                    isUpdatingDiagnostic={isUpdatingDiagnostic}
                />
            )}

            <DiagnosticInfoModal onHide={handleCloseInfoModal} show={showInfoModal} />

            <ChangeDiagnosticModal
                diagnosticOptions={diagnosticOptions}
                hasFailedToLoadDiagnostics={hasFailedToLoadDiagnostics}
                hasFailedToLoadMoreDiagnostics={hasFailedToLoadMoreDiagnostics}
                hasMoreDiagnostics={hasMoreDiagnostics}
                isLoadingDiagnostics={isLoadingDiagnostics}
                isLoadingMoreDiagnostics={isLoadingMoreDiagnostics}
                isSavingCvtChallengeDetails={cvtChallengeDetailsSaveCount > 0}
                isUpdatingDiagnostic={isUpdatingDiagnostic}
                onChangeDiagnostic={handleChangeDiagnostic}
                onHide={handleChangeDiagnosticModalHide}
                onLoadMoreDiagnostics={handleLoadMoreDiagnostics}
                onReloadDiagnostics={handleReloadDiagnostics}
                onRunNewDiagnostic={handleRunNewDiagnostic}
                show={showChangeDiagnosticModal}
            />

            <CvtPrimaryConcernsModal
                isUploading={isUploading}
                onBack={handlePrimaryConcernsModalBack}
                onClose={handlePrimaryConcernsModalClose}
                onGoToStis={handleGoToStis}
                onRunDiagnostic={handleRunDiagnostic}
                show={showPrimaryConcernsModal}
            />

            {createPortal(
                <ToastContainer
                    closeOnClick
                    draggable
                    hideProgressBar
                    newestOnTop={false}
                    pauseOnFocusLoss
                    pauseOnHover
                    position="top-right"
                    rtl={false}
                    style={toastContainerStyle}
                    theme="colored"
                    transition={Bounce}
                />,
                document.body
            )}
        </div>
    );
}
